import { useState, useEffect, useRef } from 'react';
import { 
  Title, 
  Text, 
  Button, 
  Textarea, 
  Group, 
  Stack,
  Paper,
  Box,
  Radio,
  Card,
  Progress,
  Transition,
  Container,
  rem,
  useMantineTheme,
  Image,
  FileButton,
  Avatar,
  ActionIcon,
  Divider,
  Grid,
  UnstyledButton,
  TextInput,
  Select,
  ThemeIcon,
  Popover,
  Menu
} from '@mantine/core';
import { 
  IconArrowRight, 
  IconArrowLeft, 
  IconPhoto, 
  IconVideo, 
  IconFileText, 
  IconChartBar, 
  IconMoodSmile,
  IconWorld,
  IconChevronDown,
  IconX,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconUser
} from '@tabler/icons-react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { FormData, PostType, PollOption } from '@/types';
import { generatePersonas } from '@/api/analysis';
import logger from '@/utils/logger';
import { v4 as uuidv4 } from 'uuid';

// Extend Window interface to include our custom property
declare global {
  interface Window {
    __preloadedPersonas?: Promise<any>;
  }
}

interface OnboardingProps {
  formData: FormData;
  onFormChange: (data: Partial<FormData>) => void;
  onComplete: () => void;
}

interface QuestionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  isActive: boolean;
  onNext: () => void;
  onPrev?: () => void;
  nextDisabled?: boolean;
}

// TypeForm-style question component
const Question = ({ 
  title, 
  description, 
  children, 
  isActive, 
  onNext, 
  onPrev, 
  nextDisabled = false 
}: QuestionProps) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Set mounted after a slight delay for better animation
    if (isActive) {
      const timer = setTimeout(() => {
        setMounted(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setMounted(false);
    }
  }, [isActive]);

  if (!isActive) return null;

  return (
    <Transition 
      mounted={mounted} 
      transition="slide-up" 
      duration={400} 
      timingFunction="ease"
    >
      {(styles) => (
        <Box style={{ ...styles, height: '100%' }}>
          <Stack gap="xl" justify="space-between" h="100%">
            <Stack gap="md">
              <Title order={2}>{title}</Title>
              {description && <Text c="dimmed">{description}</Text>}
              <Box pt="md">{children}</Box>
            </Stack>
            
            <Group justify="space-between">
              {onPrev ? (
                <Button 
                  variant="outline" 
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={onPrev}
                >
                  Back
                </Button>
              ) : <div />}
              
              <Button 
                disabled={nextDisabled} 
                rightSection={<IconArrowRight size={16} />}
                onClick={onNext}
              >
                {title === "Create your LinkedIn post" ? "Test this post" : "Continue"}
              </Button>
            </Group>
          </Stack>
        </Box>
      )}
    </Transition>
  );
};

// Choice card for multiple choice selections
const ChoiceCard = ({ 
  value, 
  label, 
  description, 
  isSelected, 
  onClick 
}: { 
  value: string; 
  label: string; 
  description?: string; 
  isSelected: boolean; 
  onClick: () => void; 
}) => {
  const theme = useMantineTheme();
  
  return (
    <Card 
      p="lg" 
      radius="md" 
      withBorder 
      tabIndex={0}
      style={{ 
        cursor: 'pointer',
        borderColor: isSelected ? theme.colors.blue[5] : undefined,
        backgroundColor: isSelected ? theme.colors.blue[0] : undefined,
        transform: isSelected ? 'translateY(-2px)' : undefined,
        boxShadow: isSelected ? `${rem(0)} ${rem(2)} ${rem(4)} rgba(0, 0, 0, 0.05)` : undefined,
        transition: 'all 0.2s ease'
      }}
      onClick={onClick}
      onKeyDown={(e) => {
        // Allow selecting with Enter or Space keys
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Group align="center" wrap="nowrap">
        <Radio 
          checked={isSelected}
          value={value} 
          label={null}
          onChange={() => {}}
          onClick={(e) => e.stopPropagation()}
          style={{ pointerEvents: 'none' }}
        />
        <div>
          <Text fw={500} size="lg">{label}</Text>
          {description && (
            <Text c="dimmed" mt="xs" size="sm">
              {description}
            </Text>
          )}
        </div>
      </Group>
    </Card>
  );
};

const Onboarding = ({ formData, onFormChange, onComplete }: OnboardingProps) => {
  // Change step counting to have job and industry as separate steps
  const [step, setStep] = useState(0);
  // Remove backgroundStep state since we're converting to main steps
  const theme = useMantineTheme();
  const [selectionInProgress, setSelectionInProgress] = useState(false);
  const [focusedOption, setFocusedOption] = useState<number | null>(null);
  const isActive = true; // Used by the keyboard handler
  const [personaGenerationStarted, setPersonaGenerationStarted] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const jobContainerRef = useRef<HTMLDivElement | null>(null);
  const [newPollOption, setNewPollOption] = useState('');
  const [editingPollOptionId, setEditingPollOptionId] = useState<string | null>(null);
  // Add state for dynamic font size
  const [dynamicFontSize, setDynamicFontSize] = useState(168);
  // Add state for custom hashtag input
  const [hashtagInput, setHashtagInput] = useState('');

  // Function to calculate font size based on container width and text
  const calculateOptimalFontSize = () => {
    if (!textareaRef.current || !jobContainerRef.current || !formData.userRole) {
      return 168; // Default size for empty text
    }
    
    const containerWidth = jobContainerRef.current.offsetWidth - 40; // Account for padding
    const text = formData.userRole;
    const baseSize = 168;
    const minSize = 14;
    
    // Create a temporary span to measure text width
    const span = document.createElement('span');
    span.style.fontSize = `${baseSize}px`;
    span.style.fontWeight = '800';
    span.style.position = 'absolute';
    span.style.visibility = 'hidden';
    span.style.whiteSpace = 'nowrap';
    span.textContent = text;
    document.body.appendChild(span);
    
    // Get text width at base font size
    const textWidth = span.offsetWidth;
    
    // Calculate the scaling factor needed
    let scaleFactor = 1;
    if (textWidth > containerWidth) {
      scaleFactor = containerWidth / textWidth;
    }
    
    // Clean up
    document.body.removeChild(span);
    
    // Apply scaling factor to get new font size (but not smaller than minSize)
    return Math.max(minSize, Math.floor(baseSize * scaleFactor));
  };
  
  // Update font size when job text changes
  useEffect(() => {
    if (step === 0 && jobContainerRef.current) {
      // Use requestAnimationFrame to ensure DOM measurements are accurate
      requestAnimationFrame(() => {
        const newSize = calculateOptimalFontSize();
        setDynamicFontSize(newSize);
      });
    }
  }, [formData.userRole, step]);
  
  // Also recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (step === 0 && jobContainerRef.current) {
        const newSize = calculateOptimalFontSize();
        setDynamicFontSize(newSize);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [step, formData.userRole]);

  // Check for returnToPostCreation flag and skip to post creation step (now step 5)
  useEffect(() => {
    const returnToPostCreation = sessionStorage.getItem('returnToPostCreation');
    if (returnToPostCreation === 'true') {
      // Jump directly to post creation step (now step 5)
      setStep(5);
      // Clear the flag
      sessionStorage.removeItem('returnToPostCreation');
      logger.info('Returned to post creation with improved content');
    }
  }, []);

  // Initialize poll options if not already set
  useEffect(() => {
    console.log('useEffect triggered - postType changed to:', formData.postType);
    
    if (formData.postType === 'poll' && (!formData.pollOptions || formData.pollOptions.length === 0)) {
      console.log('Initializing poll options in useEffect');
      // Initialize with two empty options
      const defaultOptions: PollOption[] = [
        { id: uuidv4(), text: '' },
        { id: uuidv4(), text: '' }
      ];
      
      console.log('Created default options:', defaultOptions);
      
      onFormChange({ 
        pollOptions: defaultOptions,
        pollDuration: '1 week'
      });
      
      // Set the first option to be edited by default
      if (defaultOptions.length > 0) {
        console.log('Setting editing poll option ID:', defaultOptions[0].id);
        setEditingPollOptionId(defaultOptions[0].id);
      }
    }
  }, [formData.postType]);

  const handleNextStep = () => {
    // Update to reflect the new last step (now step 5)
    if (step === 5) {
      onComplete();
      return;
    }
    
    // When moving from target audience (now step 4) to post creation (now step 5)
    // Start persona generation in the background
    if (step === 4) {
      try {
        // Only start once
        if (!personaGenerationStarted) {
          setPersonaGenerationStarted(true);
          
          logger.info('==========================================');
          logger.info('STARTING EARLY BACKGROUND PERSONA GENERATION');
          logger.info('==========================================');
          
          // Start persona generation in the background
          const preloadedPersonas = generatePersonas(formData);
          
          // Store in window object for the App component to access later
          window.__preloadedPersonas = preloadedPersonas;
          
          // Log that it's been started
          logger.info('Early background persona generation initiated from Onboarding');
          console.log('Early background persona generation started!', { 
            timestamp: new Date().toISOString(),
            industry: formData.userIndustry,
            role: formData.userRole,
            step: 'After target audience selection'
          });
        }
      } catch (error) {
        logger.error('Failed to start early persona generation:', error);
      }
    }
    
    setStep((current) => current + 1);
  };

  const handlePrevStep = () => {
    setStep((current) => current - 1);
  };

  // Job validation
  const isJobValid = Boolean(formData.userRole);
  
  // Industry validation
  const isIndustryValid = Boolean(formData.userIndustry);

  // Function to handle option selection with delay
  const handleOptionSelect = (field: keyof FormData, value: string) => {
    setSelectionInProgress(true);
    onFormChange({ [field]: value });
    
    // If this is the target audience selection (now step 4), initiate persona generation
    if (step === 4 && field === 'targetAudience' && !personaGenerationStarted) {
      try {
        setPersonaGenerationStarted(true);
        
        logger.info('==========================================');
        logger.info('STARTING IMMEDIATE BACKGROUND PERSONA GENERATION');
        logger.info('==========================================');
        
        // Start persona generation in the background
        const preloadedPersonas = generatePersonas(formData);
        
        // Store in window object for the App component to access later
        window.__preloadedPersonas = preloadedPersonas;
        
        logger.info('Immediate background persona generation initiated from option selection');
      } catch (error) {
        logger.error('Failed to start immediate persona generation:', error);
      }
    }
    
    // Delay before moving to the next step
    setTimeout(() => {
      setSelectionInProgress(false);
      handleNextStep();
    }, 500); // 500ms delay to show the selection before moving on
  };

  // Job text change handler
  const handleJobTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onFormChange({ userRole: newValue });
  };

  // Industry selection handler
  const handleIndustrySelect = (industry: string) => {
    setSelectionInProgress(true);
    onFormChange({ userIndustry: industry });
    
    // If they selected "other", don't auto advance
    if (industry === 'other') {
      setSelectionInProgress(false);
      return;
    }
    
    // Only auto-advance if they've selected an industry
    if (industry) {
      // Delay before moving to the next step
      setTimeout(() => {
        setSelectionInProgress(false);
        handleNextStep();
      }, 500);
    } else {
      setSelectionInProgress(false);
    }
  };

  // Purpose options
  const purposeOptions = [
    { value: 'share_knowledge', label: 'Share Knowledge', description: 'Educate your audience with valuable information and insights' },
    { value: 'build_brand', label: 'Build Personal Brand', description: 'Enhance your professional reputation and visibility' },
    { value: 'generate_leads', label: 'Generate Leads', description: 'Attract potential clients or customers to your business' },
    { value: 'networking', label: 'Networking', description: 'Connect with professionals in your industry' },
    { value: 'promote_product', label: 'Promote Product/Service', description: 'Showcase your product or service offerings' },
    { value: 'job_hunting', label: 'Job Hunting', description: 'Position yourself for new career opportunities' }
  ];

  // Tone options
  const toneOptions = [
    { value: 'professional', label: 'Professional', description: 'Formal and business-oriented language' },
    { value: 'casual', label: 'Casual', description: 'Relaxed, conversational tone that feels approachable' },
    { value: 'inspirational', label: 'Inspirational', description: 'Motivational content that inspires action' },
    { value: 'educational', label: 'Educational', description: 'Informative content focused on teaching concepts' },
    { value: 'authoritative', label: 'Authoritative', description: 'Confident tone establishing thought leadership' },
    { value: 'humorous', label: 'Humorous', description: 'Light-hearted content with appropriate humor' }
  ];

  // Audience options
  const audienceOptions = [
    { value: 'industry_peers', label: 'Industry Peers', description: 'Professionals working in the same field as you' },
    { value: 'potential_clients', label: 'Potential Clients', description: 'Decision-makers who might buy your products/services' },
    { value: 'hiring_managers', label: 'Hiring Managers', description: 'Recruiters and hiring professionals looking for talent' },
    { value: 'general_professional', label: 'General Professional Network', description: 'A broad audience of various professionals' },
    { value: 'thought_leaders', label: 'Thought Leaders', description: 'Influencers and innovators in your industry' }
  ];

  // Industry options
  const industries = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'other', label: 'Other' }
  ];

  // Check if content is provided
  const hasContent = Boolean(
    formData.postContent.trim() || 
    formData.files.length > 0 ||
    formData.postType === 'poll'
  );

  // Add debugging for content check
  useEffect(() => {
    if (formData.postType === 'poll') {
      console.log('Content status:', {
        hasContent,
        postContent: Boolean(formData.postContent.trim()),
        hasFiles: formData.files.length > 0,
        isPollType: formData.postType === 'poll',
        pollOptions: formData.pollOptions
      });
    }
  }, [formData.postType, formData.postContent, formData.files, formData.pollOptions, hasContent]);

  // Progress percentage - now with 6 steps (0-5)
  const progress = ((step + 1) / 6) * 100;

  // Handle emoji selection
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    // Get current cursor position
    const textarea = textareaRef.current;
    const cursorPosition = textarea?.selectionStart || formData.postContent.length;
    
    // Insert emoji at cursor position
    const textBeforeCursor = formData.postContent.substring(0, cursorPosition);
    const textAfterCursor = formData.postContent.substring(cursorPosition);
    const newText = textBeforeCursor + emojiData.emoji + textAfterCursor;
    
    onFormChange({ postContent: newText });
    setEmojiPickerOpen(false);
    
    // Focus the textarea and place cursor after the emoji
    setTimeout(() => {
      if (textarea) {
        textarea.focus();
        const newCursorPosition = cursorPosition + emojiData.emoji.length;
        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  // Poll related functions
  const handleAddPollOption = () => {
    if (!newPollOption.trim()) return;
    
    const newOption: PollOption = {
      id: uuidv4(),
      text: newPollOption
    };
    
    const currentOptions = formData.pollOptions || [];
    if (currentOptions.length >= 4) return; // LinkedIn limits polls to 4 options
    
    onFormChange({
      pollOptions: [...currentOptions, newOption]
    });
    
    setNewPollOption('');
  };
  
  const handleUpdatePollOption = (id: string, text: string) => {
    const currentOptions = formData.pollOptions || [];
    const updatedOptions = currentOptions.map(option => 
      option.id === id ? { ...option, text } : option
    );
    
    onFormChange({
      pollOptions: updatedOptions
    });
  };
  
  const handleRemovePollOption = (id: string) => {
    const currentOptions = formData.pollOptions || [];
    if (currentOptions.length <= 2) return; // Keep at least 2 options
    
    const updatedOptions = currentOptions.filter(option => option.id !== id);
    
    onFormChange({
      pollOptions: updatedOptions
    });
  };
  
  const handlePollDurationChange = (duration: string | null) => {
    if (!duration) return;
    
    onFormChange({
      pollDuration: duration as '1 day' | '3 days' | '1 week' | '2 weeks'
    });
  };

  // Set up keyboard event listener - placed after all options are defined
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive) return;
      
      // Handle navigation between questions with left/right arrows
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        // Only navigate forward if current step is complete
        if ((step === 0 && isJobValid) ||
            (step === 1 && isIndustryValid) ||
            (step === 2 && formData.purpose) ||
            (step === 3 && formData.tone) ||
            (step === 4 && formData.targetAudience) ||
            (step === 5 && hasContent)) {
          handleNextStep();
        }
        return;
      }
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (step > 0) {
          handlePrevStep();
        }
        return;
      }
      
      // Handle keyboard navigation for all selection screens including industry (step 1)
      if (step === 1 || step === 2 || step === 3 || step === 4) {
        // Determine which options to use based on current step
        const options = step === 1 ? industries : 
                        step === 2 ? purposeOptions : 
                        step === 3 ? toneOptions : 
                        audienceOptions;
        
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setFocusedOption(prev => {
              if (prev === null) return 0;
              return Math.min(prev + 1, options.length - 1);
            });
            break;
          case 'ArrowUp':
            e.preventDefault();
            setFocusedOption(prev => {
              if (prev === null) return options.length - 1;
              return Math.max(prev - 1, 0);
            });
            break;
          case 'Enter':
            e.preventDefault();
            if (focusedOption !== null) {
              const option = options[focusedOption];
              
              if (step === 1) {
                // Special handling for industry selection
                handleIndustrySelect(option.value);
              } else {
                // For other steps
                const field = step === 2 ? 'purpose' : 
                            step === 3 ? 'tone' : 
                            'targetAudience';
                handleOptionSelect(field as keyof FormData, option.value);
              }
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step, focusedOption, industries, purposeOptions, toneOptions, audienceOptions, isActive, isJobValid, isIndustryValid, formData.purpose, formData.tone, formData.targetAudience, hasContent]);

  // Reset focused option when step changes
  useEffect(() => {
    setFocusedOption(null);
  }, [step]);

  // Function to add a hashtag to the post content
  const addHashtag = (hashtag: string) => {
    // Clean the hashtag text
    let cleanedHashtag = hashtag.trim();
    
    // Skip if empty
    if (!cleanedHashtag) return;
    
    // Add # if not present
    if (!cleanedHashtag.startsWith('#')) {
      cleanedHashtag = '#' + cleanedHashtag;
    }
    
    // Remove spaces and special characters (except for #)
    cleanedHashtag = cleanedHashtag.replace(/[^\w#]/g, '');
    
    // Add the hashtag to the post content
    onFormChange({ 
      postContent: formData.postContent + ' ' + cleanedHashtag
    });
    
    // Clear the input
    setHashtagInput('');
  };

  return (
    <Paper p="md" radius="md" withBorder className='responsive-container' style={{ 
      minHeight: 'calc(100vh - 48px)', 
      margin: '24px auto',
      overflow: 'hidden',
    }}>
      <Progress 
        value={progress} 
        size="sm" 
        radius="xl" 
        mb="lg" 
        color="blue" 
      />
      
      <Box style={{ height: 'calc(100% - 40px)' }}>
        {/* Question 0: Job */}
        <Question 
          title="What is your job?"
          description="Your job helps us understand your professional context"
          isActive={step === 0}
          onNext={handleNextStep}
          onPrev={step === 0 ? undefined : handlePrevStep}
          nextDisabled={!isJobValid}
        >
          <Box 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: 'calc(50vh - 100px)',
              width: '100%',
              boxSizing: 'border-box',
              padding: '0 20px'
            }}
            ref={jobContainerRef}
          >
            <Textarea
              placeholder=""
              value={formData.userRole || ''}
              onChange={handleJobTextChange}
              autoFocus
              autosize
              minRows={1}
              maxRows={1}
              ref={textareaRef}
              styles={{
                root: {
                  width: '100%'
                },
                input: {
                  fontSize: rem(dynamicFontSize),
                  fontWeight: 800,
                  textAlign: 'center',
                  border: 'none',
                  background: 'transparent',
                  boxShadow: 'none',
                  padding: 0,
                  margin: 0,
                  caretColor: 'black',
                  lineHeight: 1.2,
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                },
                wrapper: {
                  width: '100%',
                  border: 'none',
                  background: 'transparent'
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && formData.userRole) {
                  e.preventDefault();
                  handleNextStep();
                }
              }}
            />
          </Box>
        </Question>

        {/* Question 1: Industry */}
        <Question 
          title="What industry do you work in?"
          description="This helps us create personas from your industry and related fields"
          isActive={step === 1}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          nextDisabled={!isIndustryValid}
        >
          <Stack gap="md">
            <Stack gap="xs">
              {industries.map((option, index) => (
                <ChoiceCard
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  isSelected={formData.userIndustry === option.value || focusedOption === index}
                  onClick={() => !selectionInProgress && handleIndustrySelect(option.value)}
                />
              ))}
            </Stack>
            
            {formData.userIndustry === 'other' && (
              <Box mt="md">
                <TextInput
                  label="Please specify your industry:"
                  placeholder="Enter your industry..."
                  value={formData.userIndustryCustom || ''}
                  onChange={(e) => onFormChange({ userIndustryCustom: e.target.value })}
                  autoFocus
                />
              </Box>
            )}
            
            <Text size="sm" c="dimmed" mt="lg">
              This information is used to create more relevant synthetic personas and is not shared with any third parties.
              We don't store your personal information beyond your current session.
            </Text>
          </Stack>
        </Question>

        {/* Question 2: Purpose (formerly Question 1) */}
        <Question 
          title="What's the purpose of your LinkedIn post?"
          description="This helps us analyze if your content will achieve these goals and create relevant synthetic personas."
          isActive={step === 2}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          nextDisabled={!formData.purpose}
        >
          <Stack gap="md">
            {purposeOptions.map((option, index) => (
              <ChoiceCard
                key={option.value}
                value={option.value}
                label={option.label}
                description={option.description}
                isSelected={formData.purpose === option.value || focusedOption === index}
                onClick={() => !selectionInProgress && handleOptionSelect('purpose', option.value)}
              />
            ))}
            
            {/* Custom purpose option */}
            <Textarea
              label="Or describe your own purpose:"
              placeholder="Describe the specific goals for your post..."
              value={typeof formData.purpose === 'string' && !purposeOptions.some(o => o.value === formData.purpose) ? formData.purpose : ''}
              onChange={(e) => onFormChange({ purpose: e.target.value })}
              minRows={3}
            />
          </Stack>
        </Question>
        
        {/* Question 3: Tone (formerly Question 2) */}
        <Question 
          title="What tone are you aiming for?"
          description="The tone affects how your audience perceives your message and helps create more realistic synthetic personas."
          isActive={step === 3}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          nextDisabled={!formData.tone}
        >
          <Stack gap="md">
            {toneOptions.map((option, index) => (
              <ChoiceCard
                key={option.value}
                value={option.value}
                label={option.label}
                description={option.description}
                isSelected={formData.tone === option.value || focusedOption === index}
                onClick={() => !selectionInProgress && handleOptionSelect('tone', option.value)}
              />
            ))}
            
            {/* Custom tone option */}
            <Textarea
              label="Or describe your preferred tone:"
              placeholder="Describe a specific tone not listed above..."
              value={typeof formData.tone === 'string' && !toneOptions.some(o => o.value === formData.tone) ? formData.tone : ''}
              onChange={(e) => onFormChange({ tone: e.target.value })}
              minRows={2}
            />
          </Stack>
        </Question>
        
        {/* Question 4: Target Audience (formerly Question 3) */}
        <Question 
          title="Who is your target audience?"
          description="Understanding your audience helps us generate realistic synthetic personas tailored to your target demographic."
          isActive={step === 4}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          nextDisabled={!formData.targetAudience}
        >
          <Stack gap="md">
            {audienceOptions.map((option, index) => (
              <ChoiceCard
                key={option.value}
                value={option.value}
                label={option.label}
                description={option.description}
                isSelected={formData.targetAudience === option.value || focusedOption === index}
                onClick={() => !selectionInProgress && handleOptionSelect('targetAudience', option.value)}
              />
            ))}
            
            {/* Custom audience option */}
            <Textarea
              label="Or describe your specific audience:"
              placeholder="e.g., Senior marketing executives at enterprise SaaS companies..."
              value={typeof formData.targetAudience === 'string' && !audienceOptions.some(o => o.value === formData.targetAudience) ? formData.targetAudience : ''}
              onChange={(e) => onFormChange({ targetAudience: e.target.value })}
              minRows={3}
            />
          </Stack>
        </Question>
        
        {/* Question 5: LinkedIn Post Creator (formerly Question 4) */}
        <Question 
          title="Create your LinkedIn post"
          description="Just like on LinkedIn, create your post exactly as you would publish it."
          isActive={step === 5}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          nextDisabled={!hasContent}
        >
          <Card p={0} radius="md" withBorder style={{ overflow: 'visible' }}>
            <Stack gap={0}>
              {/* Header */}
              <Group p="md" align="center">
                <Avatar 
                  radius="xl" 
                  size="md" 
                  src={null}
                  color="gray"
                >
                  <IconUser size={24} stroke={1.5} />
                </Avatar>
                <Stack gap={5}>
                  <Text fw={500}>You</Text>
                  <Group gap={6}>
                    <UnstyledButton 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px',
                        fontSize: rem(12),
                        color: theme.colors.gray[6] 
                      }}
                    >
                      <IconWorld size={14} />
                      <Text size="xs">Anyone</Text>
                      <IconChevronDown size={14} />
                    </UnstyledButton>
                  </Group>
                </Stack>
              </Group>
              
              <Divider />
              
              {/* Main content area */}
              <Box p="md" style={{ minHeight: '200px' }}>
                <Textarea
                  placeholder="What do you want to talk about?"
                  value={formData.postContent}
                  onChange={(e) => onFormChange({ postContent: e.target.value })}
                  minRows={5}
                  maxRows={10}
                  autosize
                  ref={textareaRef}
                  styles={{
                    input: {
                      border: 'none',
                      padding: 0,
                      backgroundColor: 'transparent',
                      fontSize: rem(16),
                      '&:focus': {
                        outline: 'none',
                        border: 'none'
                      }
                    }
                  }}
                />
                
                {/* Media Preview */}
                {formData.files.length > 0 && (
                  <Box mt="md">
                    {formData.files.some(file => file.type?.includes('image')) && (
                      <Grid gutter="xs" mt="md">
                        {formData.files
                          .filter(file => file.type?.includes('image'))
                          .map((file, index) => (
                            <Grid.Col key={index} span={{ base: 12, xs: formData.files.filter(f => f.type?.includes('image')).length > 1 ? 6 : 12 }}>
                              <Box style={{ position: 'relative' }}>
                                <Image
                                  src={URL.createObjectURL(file)}
                                  alt={`Image ${index + 1}`}
                                  radius="md"
                                  h={200}
                                />
                                <ActionIcon
                                  variant="filled"
                                  color="dark"
                                  radius="xl"
                                  size="sm"
                                  style={{ 
                                    position: 'absolute', 
                                    top: 8, 
                                    right: 8,
                                    opacity: 0.7
                                  }}
                                  onClick={() => {
                                    const updatedFiles = [...formData.files];
                                    updatedFiles.splice(formData.files.indexOf(file), 1);
                                    onFormChange({ files: updatedFiles });
                                  }}
                                >
                                  <IconX size={14} />
                                </ActionIcon>
                              </Box>
                            </Grid.Col>
                          ))}
                      </Grid>
                    )}
                    
                    {formData.files.some(file => file.type?.includes('video')) && (
                      <Card withBorder p="sm" radius="md" mt="md">
                        <Group justify="apart">
                          <Group>
                            <IconVideo size={20} stroke={1.5} />
                            <Text size="sm">
                              {formData.files.find(file => file.type?.includes('video'))?.name}
                            </Text>
                          </Group>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => {
                              const updatedFiles = formData.files.filter(file => !file.type?.includes('video'));
                              onFormChange({ files: updatedFiles });
                            }}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Group>
                      </Card>
                    )}
                    
                    {formData.files.some(file => 
                      file.name?.endsWith('.pdf') || 
                      file.name?.endsWith('.ppt') || 
                      file.name?.endsWith('.pptx') || 
                      file.name?.endsWith('.doc') || 
                      file.name?.endsWith('.docx')
                    ) && (
                      <Card withBorder p="sm" radius="md" mt="md">
                        <Group justify="apart">
                          <Group>
                            <IconFileText size={20} stroke={1.5} />
                            <Text size="sm">
                              {formData.files.find(file => 
                                file.name?.endsWith('.pdf') || 
                                file.name?.endsWith('.ppt') || 
                                file.name?.endsWith('.pptx') || 
                                file.name?.endsWith('.doc') || 
                                file.name?.endsWith('.docx')
                              )?.name}
                            </Text>
                          </Group>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => {
                              const updatedFiles = formData.files.filter(file => 
                                !file.name?.endsWith('.pdf') && 
                                !file.name?.endsWith('.ppt') && 
                                !file.name?.endsWith('.pptx') && 
                                !file.name?.endsWith('.doc') && 
                                !file.name?.endsWith('.docx')
                              );
                              onFormChange({ files: updatedFiles });
                            }}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Group>
                      </Card>
                    )}
                  </Box>
                )}
                
                {/* Debug log for conditional rendering */}
                {(() => {
                  console.log('Conditional rendering check - postType:', formData.postType, 'Should render poll?', formData.postType === 'poll');
                  return null;
                })()}
                
                {/* Poll UI - moved outside of files length condition */}
                {formData.postType === 'poll' && (
                  <Box mt="md">
                    <Card withBorder p="md" radius="md">
                      <Group justify="space-between" mb="md">
                        <Text fw={500}>Poll</Text>
                        <Select
                          data={['1 day', '3 days', '1 week', '2 weeks']}
                          value={formData.pollDuration || '1 week'}
                          onChange={handlePollDurationChange}
                          size="xs"
                          style={{ width: 100 }}
                        />
                      </Group>
                      
                      <Stack gap="xs">
                        {(formData.pollOptions || []).map((option) => (
                          <Group key={option.id} style={{ 
                            border: `1px solid ${theme.colors.gray[3]}`, 
                            borderRadius: theme.radius.sm,
                            padding: '8px 12px'
                          }}>
                            <Radio
                              value={option.id}
                              disabled
                              checked={false}
                            />
                            
                            {editingPollOptionId === option.id ? (
                              <TextInput
                                style={{ flexGrow: 1 }}
                                value={option.text}
                                placeholder="Enter poll option"
                                onChange={(e) => handleUpdatePollOption(option.id, e.target.value)}
                                autoFocus
                                onBlur={() => setEditingPollOptionId(null)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    setEditingPollOptionId(null);
                                  }
                                }}
                              />
                            ) : (
                              <Text 
                                size="sm" 
                                style={{ flexGrow: 1, cursor: 'pointer' }}
                                onClick={() => setEditingPollOptionId(option.id)}
                              >
                                {option.text || 'Click to edit this option'}
                              </Text>
                            )}
                            
                            <Menu shadow="md" width={200} position="bottom-end">
                              <Menu.Target>
                                <ActionIcon size="sm" variant="subtle">
                                  <IconDots size={14} />
                                </ActionIcon>
                              </Menu.Target>
                              
                              <Menu.Dropdown>
                                <Menu.Item 
                                  leftSection={<IconPencil size={14} />}
                                  onClick={() => setEditingPollOptionId(option.id)}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item 
                                  leftSection={<IconTrash size={14} />}
                                  color="red"
                                  disabled={(formData.pollOptions || []).length <= 2}
                                  onClick={() => handleRemovePollOption(option.id)}
                                >
                                  Delete
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </Group>
                        ))}
                        
                        {(formData.pollOptions || []).length < 4 && (
                          <Group style={{ 
                            border: `1px dashed ${theme.colors.gray[4]}`, 
                            borderRadius: theme.radius.sm,
                            padding: '8px 12px'
                          }}>
                            <Radio
                              value="new"
                              disabled
                              checked={false}
                            />
                            <TextInput
                              style={{ flexGrow: 1 }}
                              placeholder="Add another option"
                              value={newPollOption}
                              onChange={(e) => setNewPollOption(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && newPollOption.trim()) {
                                  handleAddPollOption();
                                }
                              }}
                            />
                            <Button 
                              size="xs" 
                              disabled={!newPollOption.trim()}
                              onClick={handleAddPollOption}
                            >
                              Add
                            </Button>
                          </Group>
                        )}
                      </Stack>
                      
                      <Group justify="space-between" mt="md">
                        <Text size="xs" c="dimmed">Poll duration: {formData.pollDuration || '1 week'}</Text>
                        <Button 
                          size="xs" 
                          variant="subtle" 
                          color="gray"
                          onClick={() => onFormChange({ postType: 'text' })}
                        >
                          Cancel Poll
                        </Button>
                      </Group>
                    </Card>
                  </Box>
                )}
              </Box>
              
              <Divider />
              
              {/* Media and action buttons */}
              <Group p="md" justify="apart">
                <Group gap={8}>
                  <FileButton 
                    onChange={(file) => {
                      if (file) {
                        const newFiles = [...formData.files, file];
                        onFormChange({ 
                          files: newFiles,
                          postType: 'image'
                        });
                      }
                    }}
                    accept="image/png,image/jpeg,image/gif"
                  >
                    {(props) => (
                      <ActionIcon {...props} variant="subtle" size="lg" radius="xl" color="gray">
                        <IconPhoto size={20} />
                      </ActionIcon>
                    )}
                  </FileButton>
                  
                  <FileButton 
                    onChange={(file) => {
                      if (file) {
                        const newFiles = [...formData.files, file];
                        onFormChange({ 
                          files: newFiles,
                          postType: 'video'
                        });
                      }
                    }}
                    accept="video/mp4,video/quicktime"
                  >
                    {(props) => (
                      <ActionIcon {...props} variant="subtle" size="lg" radius="xl" color="gray">
                        <IconVideo size={20} />
                      </ActionIcon>
                    )}
                  </FileButton>
                  
                  <FileButton 
                    onChange={(file) => {
                      if (file) {
                        const newFiles = [...formData.files, file];
                        onFormChange({ 
                          files: newFiles,
                          postType: 'document'
                        });
                      }
                    }}
                    accept=".pdf,.ppt,.pptx,.doc,.docx"
                  >
                    {(props) => (
                      <ActionIcon {...props} variant="subtle" size="lg" radius="xl" color="gray">
                        <IconFileText size={20} />
                      </ActionIcon>
                    )}
                  </FileButton>
                  
                  <ActionIcon 
                    variant="subtle" 
                    size="lg" 
                    radius="xl" 
                    color={formData.postType === 'poll' ? 'blue' : 'gray'}
                    onClick={() => {
                      console.log('Poll button clicked');
                      // Initialize poll when clicked
                      const defaultOptions = [
                        { id: uuidv4(), text: '' },
                        { id: uuidv4(), text: '' }
                      ];
                      
                      // Log the current state and what we're setting
                      console.log('Current postType:', formData.postType);
                      console.log('Setting poll options:', defaultOptions);
                      
                      // Explicitly set postType to trigger the useEffect
                      onFormChange({ 
                        postType: 'poll',
                        pollOptions: defaultOptions,
                        pollDuration: '1 week'
                      });
                      
                      // Force debug check after state update
                      setTimeout(() => {
                        console.log('After update - postType:', formData.postType);
                        console.log('After update - pollOptions:', formData.pollOptions);
                        console.log('RENDER CHECK - Should poll UI be visible?', formData.postType === 'poll');
                        console.log('RENDER CHECK - Current step:', step);
                        console.log('RENDER CHECK - Is at post creation step?', step === 5);
                      }, 100);
                      
                      // Set the first option to be edited by default
                      setEditingPollOptionId(defaultOptions[0].id);
                    }}
                  >
                    <IconChartBar size={20} />
                  </ActionIcon>
                  
                  <Popover 
                    opened={emojiPickerOpen} 
                    onChange={setEmojiPickerOpen}
                    position="top"
                    shadow="md"
                    withArrow
                  >
                    <Popover.Target>
                      <ActionIcon 
                        variant="subtle" 
                        size="lg" 
                        radius="xl" 
                        color="gray"
                        onClick={() => setEmojiPickerOpen((o) => !o)}
                      >
                        <IconMoodSmile size={20} />
                      </ActionIcon>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <EmojiPicker onEmojiClick={handleEmojiClick} width={320} height={400} />
                    </Popover.Dropdown>
                  </Popover>
                </Group>
                
                <Group>
                  <Button
                    radius="xl"
                    disabled={!hasContent}
                    color="blue"
                    onClick={handleNextStep}
                    style={{ visibility: 'hidden' }}
                  >
                    Test this post
                  </Button>
                </Group>
              </Group>
              
              {/* Hashtag input */}
              {formData.postContent && (
                <Box px="md" pb="md">
                  <Text size="xs" fw={500} c="dimmed" mb={8}>Hashtags:</Text>
                  <TextInput
                    placeholder="Add hashtag"
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && hashtagInput.trim()) {
                        e.preventDefault();
                        addHashtag(hashtagInput);
                      }
                    }}
                    size="sm"
                    radius="xl"
                    rightSection={
                      <ActionIcon 
                        size="sm" 
                        radius="xl" 
                        variant="filled" 
                        color="blue"
                        disabled={!hashtagInput.trim()}
                        onClick={() => addHashtag(hashtagInput)}
                      >
                        <IconPlus size={14} />
                      </ActionIcon>
                    }
                    styles={{
                      root: { width: '100%' },
                      input: { width: '100%' }
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Card>
        </Question>
      </Box>
      
      {/* Add custom CSS for thicker cursor */}
      <style>{`
        textarea.mantine-Textarea-input {
          caret-color: black;
          caret-width: 5px !important;
        }
      `}</style>
    </Paper>
  );
};

export default Onboarding;